import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Alert, Typography } from 'antd'

interface Props {
  children: ReactNode
  label?: string
}

interface State {
  error: Error | null
}

// ErrorBoundary surfaces runtime errors instead of unmounting the
// whole React tree (which would render a whitescreen). Lets us see
// "TypeError: Cannot read X of undefined" right in the UI, with the
// stack — much faster debug loop than chasing console errors.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Eat the message into the console for browsers too.
    console.error('[ErrorBoundary]', this.props.label || '', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <Alert
          type="error"
          showIcon
          message={`Render-Fehler${this.props.label ? ` (${this.props.label})` : ''}`}
          description={
            <>
              <Typography.Paragraph>
                <Typography.Text code>{this.state.error.message}</Typography.Text>
              </Typography.Paragraph>
              <Typography.Paragraph>
                <details>
                  <summary>Stack-Trace</summary>
                  <pre className="content-pre text-xs">
                    {this.state.error.stack}
                  </pre>
                </details>
              </Typography.Paragraph>
            </>
          }
        />
      )
    }
    return this.props.children
  }
}
